'use client';

import React, { Children, cloneElement, isValidElement } from 'react';
import { preventOrphans, balanceHeadline } from '@/lib/typography-utils';
import { IconCard } from './IconCard';

interface FeatureGridProps {
  children: React.ReactNode;
  columns?: '2' | '3' | '4' | '5' | '6';
  className?: string;
  variant?: 'default' | 'contained' | 'elegant' | 'compact' | 'showcase';
  gap?: 'tight' | 'normal' | 'relaxed' | 'loose';
  animate?: boolean;
  stagger?: boolean;
  title?: string;
  description?: string;
}

export const FeatureGrid: React.FC<FeatureGridProps> = ({ 
  children, 
  columns = '3',
  className = '',
  variant = 'default',
  gap = 'normal',
  animate = true,
  stagger = true,
  title,
  description
}) => {
  // Convert columns to number for calculations
  const columnCount = parseInt(columns, 10);
  
  // Get configuration-based sizing classes for IconCard children
  const getTitleSizeClass = () => {
    if (columnCount <= 2) return 'text-xl sm:text-2xl font-bold';
    if (columnCount === 3) return 'text-lg sm:text-xl font-bold';
    if (columnCount === 4) return 'text-base sm:text-lg font-bold';
    if (columnCount === 5) return 'text-base font-bold';
    return 'text-sm font-bold'; // 6 columns
  };
  
  const getBodySizeClass = () => {
    if (columnCount <= 2) return 'text-base';
    if (columnCount === 3) return 'text-sm sm:text-base';
    if (columnCount === 4) return 'text-xs sm:text-sm';
    return 'text-xs'; // 5+ columns
  };
  
  const getIconSizeClass = () => {
    if (columnCount <= 2) return 'w-14 h-14';
    if (columnCount === 3) return 'w-12 h-12';
    if (columnCount === 4) return 'w-10 h-10';
    if (columnCount === 5) return 'w-10 h-10';
    return 'w-8 h-8'; // 6 columns
  };
  
  const titleSizeClass = getTitleSizeClass();
  const bodySizeClass = getBodySizeClass();
  const iconSizeClass = getIconSizeClass();
  // Responsive grid classes - improved breakpoints
  const gridClasses = {
    '2': 'grid-cols-1 md:grid-cols-2',
    '3': 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
    '6': 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6'
  };

  // Gap sizing
  const gapClasses = {
    tight: 'gap-3 lg:gap-4',
    normal: 'gap-4 lg:gap-6',
    relaxed: 'gap-6 lg:gap-8',
    loose: 'gap-8 lg:gap-10'
  };

  // Container variants for different styles
  const getContainerClasses = () => {
    switch(variant) {
      case 'contained':
        return 'bg-gradient-to-br from-neutral-50 via-white to-primary/5 rounded-md p-4 lg:p-8 border border-neutral-200/50 shadow-elevation-1';
      
      case 'elegant':
        return 'bg-white rounded-md p-4 lg:p-8 shadow-elevation-2 border border-neutral-100';
      
      case 'showcase':
        return 'bg-gradient-to-br from-primary/5 to-leadpops-pink/5 rounded-md p-4 lg:p-8 shadow-xl backdrop-blur-sm border border-primary/10';
      
      case 'compact':
        return ''; // No container styling, just the grid
      
      default:
        return ''; // Default - no container
    }
  };

  // Add staggered animation delays and configuration to children
  const enhancedChildren = Children.map(children, (child, index) => {
    if (isValidElement(child)) {
      const baseProps = {
        ...(child.props as any)
      };
      
      // Add configuration data if this is an IconCard
      if (child.type === IconCard) {
        baseProps.columnCount = columnCount;
        baseProps.titleSizeClass = titleSizeClass;
        baseProps.bodySizeClass = bodySizeClass;
        baseProps.iconSizeClass = iconSizeClass;
      }
      
      // Add animation if enabled
      if (stagger && animate) {
        baseProps.className = `${baseProps.className || ''} animate-fadeInUp`;
        baseProps.style = {
          ...baseProps.style,
          animationDelay: `${index * 100}ms`,
          animationFillMode: 'both'
        };
      }
      
      return cloneElement(child as React.ReactElement<any>, baseProps);
    }
    return child;
  });

  const containerClasses = getContainerClasses();

  return (
    <div className={`feature-grid-container my-12 mdx-component-base ${className}`}>
      {/* Optional Header Section */}
      {(title || description) && (
        <div className="text-center mb-8 lg:mb-12">
          {title && (
            <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900 mb-4" style={{ textWrap: 'balance' }}>
              {balanceHeadline(title)}
            </h2>
          )}
          {description && (
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto" style={{ textWrap: 'pretty' }}>
              {preventOrphans(description)}
            </p>
          )}
        </div>
      )}

      {/* Main Grid Container */}
      <div className={`
        ${containerClasses}
        ${animate && !stagger ? 'animate-fadeIn' : ''}
        relative
      `}>
        {/* Premium gradient overlay for showcase variant */}
        {variant === 'showcase' && (
          <div className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/50 to-white/0 rounded-md pointer-events-none opacity-50" />
        )}

        {/* Grid */}
        <div className={`
          grid 
          ${gridClasses[columns]} 
          ${gapClasses[gap]}
          relative z-10
        `}>
          {enhancedChildren}
        </div>

        {/* Subtle pattern overlay for elegant variant */}
        {variant === 'elegant' && (
          <div 
            className="absolute inset-0 opacity-5 pointer-events-none rounded-md"
            style={{
              backgroundImage: `repeating-linear-gradient(
                45deg,
                transparent,
                transparent 35px,
                rgba(41, 75, 253, 0.03) 35px,
                rgba(41, 75, 253, 0.03) 70px
              )`
            }}
          />
        )}
      </div>
    </div>
  );
};

// Feature Item Component - pairs perfectly with FeatureGrid
interface FeatureItemProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  highlight?: boolean;
  className?: string;
}

export const FeatureItem: React.FC<FeatureItemProps> = ({
  icon,
  title,
  description,
  highlight = false,
  className = ''
}) => {
  return (
    <div className={`
      feature-item min-w-0
      ${highlight ? 
        'bg-gradient-to-br from-primary/10 to-leadpops-pink/10 border-2 border-primary/20 scale-105' : 
        'bg-white border border-neutral-200'
      }
      rounded-md p-4 lg:p-6
      shadow-elevation-1 hover:shadow-elevation-2
      transform transition-all duration-300
      hover:scale-[1.02] hover:-translate-y-1
      group
      ${className}
    `}>
      {/* Icon */}
      {icon && (
        <div className="mb-4">
          <div className={`
            w-12 h-12 rounded-md flex items-center justify-center
            ${highlight ? 
              'bg-gradient-to-br from-primary to-leadpops-pink' : 
              'bg-gradient-to-br from-primary/20 to-primary/10'
            }
            shadow-lg group-hover:shadow-xl
            transform transition-all duration-300
            group-hover:scale-110 group-hover:rotate-3
          `}>
            {icon}
          </div>
        </div>
      )}

      {/* Title */}
      <h3 className={`
        component-title mb-2
        ${highlight ? 'text-primary' : 'text-neutral-900'}
        group-hover:text-primary
        transition-colors duration-300
      `} style={{ textWrap: 'balance' }}>
        {preventOrphans(title)}
      </h3>

      {/* Description */}
      <p className="component-body text-neutral-600 leading-relaxed" style={{ textWrap: 'pretty' }}>
        {preventOrphans(description)}
      </p>
    </div>
  );
};

// Add animation styles to globals.css
const animationStyles = `
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.animate-fadeInUp {
  animation: fadeInUp 0.6s ease-out;
}

.animate-fadeIn {
  animation: fadeIn 0.6s ease-out;
}
`;